import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { taskService, userService, departmentService } from '../../services/api';

const TaskForm = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    due_date: '',
    department_id: ''
  });
  
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Add filteredUsers state
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        setLoading(true);
        const [usersResponse, departmentsResponse] = await Promise.all([
          userService.getAllUsers(),
          departmentService.getAllDepartments()
        ]);
        
        // Handle the users response data structure correctly
        const usersData = usersResponse.data?.data || [];
        
        // Set all employees as both users and filtered users initially
        const employeeUsers = usersData.filter(user => user.role === 'employee');
        setUsers(employeeUsers);
        setFilteredUsers(employeeUsers); // Show all employees by default
        
        // Handle the departments response data structure
        const departmentsData = departmentsResponse.data?.data || [];
        setDepartments(departmentsData);
        
        setError('');
      } catch (err) {
        console.error('Error fetching form data:', err);
        setError('Failed to load form data. Please refresh the page or try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFormData();
  }, []);

  // Update the department filtering effect
  useEffect(() => {
    if (formData.department_id) {
      // Filter users by selected department
      const departmentUsers = users.filter(user => 
        String(user.department_id) === String(formData.department_id)
      );
      setFilteredUsers(departmentUsers);
      
      // Clear selected assignee if not in the selected department
      setSelectedAssignees(prev => {
        const stillValid = prev.filter(assigneeId => 
          departmentUsers.some(user => String(user.id) === String(assigneeId))
        );
        return stillValid.length ? stillValid : [];
      });
    } else {
      // When no department is selected, show all employees
      setFilteredUsers(users);
    }
  }, [formData.department_id, users]);

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) {
      errors.title = 'Task title is required';
    }
    if (formData.title.trim().length < 3) {
      errors.title = 'Task title must be at least 3 characters';
    }
    if (formData.due_date && new Date(formData.due_date) < new Date()) {
      errors.due_date = 'Due date cannot be in the past';
    }
    if (selectedAssignees.length === 0) {
      errors.assignees = 'Please select at least one assignee';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear selected assignees when department changes
    if (name === 'department_id') {
      setSelectedAssignees([]);
    }

    // Clear validation error when field is edited
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Updated to handle single assignee selection
  const handleAssigneeChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedAssignees(selectedValue ? [selectedValue] : []);
    if (validationErrors.assignees) {
      setValidationErrors(prev => ({ ...prev, assignees: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await taskService.createTask({
        ...formData,
        assignees: selectedAssignees,
        created_by: currentUser.id
      });
      
      // Check if the response has the expected structure
      if (response.data?.success && response.data?.data) {
        setSuccess('Task created successfully! Redirecting...');
        setTimeout(() => {
          navigate(`/tasks/${response.data.data.id}`);
        }, 1500);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error creating task:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create task';
      const errorDetails = error.response?.data?.details;
      setError(`${errorMessage}${errorDetails ? `: ${errorDetails}` : ''}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading form data...</span>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="create-task-form">
        <h2 className="form-section-title">Create New Task</h2>
        
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-4">
            <Form.Label>
              Title <span className="required-field">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter task title"
              className="dark-input"
              isInvalid={!!validationErrors.title}
            />
            {validationErrors.title && (
              <Form.Control.Feedback type="invalid">
                {validationErrors.title}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter task description"
              className="dark-input"
              style={{ minHeight: '120px' }}
            />
          </Form.Group>

          <Row>
            <Col md={6} className="mb-4">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="status-select"
                >
                  <option value="pending">⏳ Start</option>
                  <option value="in_progress">🔄 In Progress</option>
                  <option value="completed">✅ Completed</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6} className="mb-4">
              <Form.Group>
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="priority-select"
                >
                  <option value="low">🟢 Low</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="high">🔴 High</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-4">
            <Form.Label>
              Department <span className="required-field">*</span>
            </Form.Label>
            <Form.Select
              name="department_id"
              value={formData.department_id}
              onChange={handleChange}
              className="dark-input"
              isInvalid={!!validationErrors.department_id}
            >
              <option value="">Select Department</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </Form.Select>
            {validationErrors.department_id && (
              <Form.Control.Feedback type="invalid">
                {validationErrors.department_id}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>
              Assignee <span className="required-field">*</span>
            </Form.Label>
            <Form.Select
              name="assignee"
              value={selectedAssignees[0] || ''}
              onChange={handleAssigneeChange}
              className="dark-input"
              isInvalid={!!validationErrors.assignees}
            >
              <option value="">Select Assignee</option>
              {filteredUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.username}
                </option>
              ))}
            </Form.Select>
            {validationErrors.assignees && (
              <Form.Control.Feedback type="invalid">
                {validationErrors.assignees}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Due Date</Form.Label>
            <Form.Control
              type="date"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
              className="dark-input"
              isInvalid={!!validationErrors.due_date}
              min={new Date().toISOString().split('T')[0]}
            />
            {validationErrors.due_date && (
              <Form.Control.Feedback type="invalid">
                {validationErrors.due_date}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <div className="d-grid">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={submitting}
              className="py-2"
            >
              {submitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Creating Task...
                </>
              ) : (
                'Create Task'
              )}
            </Button>
          </div>
        </Form>
      </div>
    </Container>
  );
};

export default TaskForm;
